import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { GetOrganizationsResponseDto } from "@workspace/dto/organization/get-organizations";
import type { UserMutationResponseDto } from "@workspace/dto/user/mutation-response";
import type { SwitchOrganizationDto } from "@workspace/dto/user/switch-organization";
import {
  Avatar,
  AvatarImage,
  InitialsAvatarFallback,
} from "@workspace/ui/components/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@workspace/ui/components/dropdown-menu";
import { cn } from "@workspace/ui/lib/utils";
import type { AxiosResponse } from "axios";
import { CheckIcon, PlusIcon } from "lucide-react";
import { useRouter } from "next/router";
import { useState } from "react";
import { toast } from "sonner";
import type { ExtendedUser } from "@/auth";
import { CreateOrganizationModal } from "@/components/modals/organizations/create-organization";
import { usePilotSelfServeLocked } from "@/components/providers/pilot-flag-provider";
import { refreshSessionFromDatabase } from "@/lib/auth/session-refresh";
import { useStableSession } from "@/lib/auth/use-stable-session";
import { axios } from "@/lib/axios";
import { getEmployerVisibleOrganizations } from "@/lib/employer-organizations";
import { getOrgSwitchRedirectDestination } from "@/lib/org-switch-navigation";
import { toastMutationNetworkError } from "@/lib/query-failure";
import { queryKeys } from "@/lib/query-keys";

type Props = {
  isExpanded: boolean;
  selectedOrganizationId: string;
  organizations: GetOrganizationsResponseDto["organizations"];
  onOpenChange?: (open: boolean) => void;
};

export function OrganizationDropdown({
  isExpanded,
  selectedOrganizationId,
  organizations,
  onOpenChange,
}: Props) {
  const router = useRouter();
  const { data: session, update } = useStableSession();
  const queryClient = useQueryClient();
  const [menuOpen, setMenuOpen] = useState(false);
  const [isCreateOpen, setCreateOpen] = useState(false);
  const pilotSelfServeLocked = usePilotSelfServeLocked();

  const handleMenuOpenChange = (open: boolean) => {
    setMenuOpen(open);
    onOpenChange?.(open);
  };

  const openCreateModal = () => {
    setMenuOpen(false);
    onOpenChange?.(false);
    setCreateOpen(true);
  };

  const handleCreateOpenChange = (open: boolean) => {
    setCreateOpen(open);
    if (!open) {
      onOpenChange?.(false);
    }
  };
  const visibleOrganizations = getEmployerVisibleOrganizations(organizations);
  const selectedOrganization =
    visibleOrganizations.find(
      (organization) => organization.id === selectedOrganizationId
    ) ?? visibleOrganizations[0];
  const userType = (session?.user as ExtendedUser | undefined)?.type;

  const updateUserMutation = useMutation({
    mutationFn: async (data: SwitchOrganizationDto) => {
      const response = await axios.put<
        UserMutationResponseDto,
        AxiosResponse<UserMutationResponseDto>,
        SwitchOrganizationDto
      >("/account/user/switch-organization", data);
      return response.data;
    },
    onSuccess: async () => {
      await refreshSessionFromDatabase(update);
      const destination = getOrgSwitchRedirectDestination(router.asPath);
      if (destination) {
        router.replace(destination);
      } else {
        router.reload();
      }
      queryClient.invalidateQueries({
        queryKey: queryKeys.account.organizations,
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.account.user });
    },
    onError: (error) => {
      if (toastMutationNetworkError(error, "Couldn't change organization")) {
        return;
      }
      toast.error(
        "Uh Oh! Something went wrong. Failed to change organization",
        {
          description: "Failed to change organization",
        }
      );
    },
  });

  if (!selectedOrganization) {
    return null;
  }

  const selectedDisplayName = selectedOrganization.name;

  return (
    <>
      <DropdownMenu onOpenChange={handleMenuOpenChange} open={menuOpen}>
        <DropdownMenuTrigger
          aria-label={
            isExpanded
              ? undefined
              : `Switch organization: ${selectedDisplayName}`
          }
          className={cn(
            "group flex h-10 cursor-pointer items-center bg-transparent outline-none hover:bg-transparent focus:bg-transparent focus-visible:outline-none data-[state=open]:bg-transparent",
            isExpanded ? "w-full" : "w-8"
          )}
          title={isExpanded ? undefined : selectedDisplayName}
        >
          <Avatar className="size-8 shrink-0 rounded-sm group-focus-visible:ring-2 group-focus-visible:ring-ring group-focus-visible:ring-offset-2 group-focus-visible:ring-offset-background">
            <AvatarImage
              alt={selectedDisplayName}
              src={selectedOrganization.logo_url}
            />
            <InitialsAvatarFallback
              className="rounded-sm"
              name={selectedDisplayName}
            />
          </Avatar>
          {isExpanded && (
            <span className="ml-3 min-w-0 truncate text-base">
              {selectedDisplayName}
            </span>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-[220px]" side="top">
          <DropdownMenuLabel>Organizations</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {visibleOrganizations.map((organization) => (
            <DropdownMenuItem
              className="flex items-center gap-2"
              key={organization.id}
              onSelect={() => {
                if (organization.id !== selectedOrganizationId) {
                  updateUserMutation.mutate({
                    organizationId: organization.id,
                  });
                }
              }}
            >
              <Avatar className="size-8 shrink-0 rounded-sm">
                <AvatarImage
                  alt={organization.name}
                  src={organization.logo_url}
                />
                <InitialsAvatarFallback
                  className="rounded-sm"
                  name={organization.name}
                />
              </Avatar>
              <span className="min-w-0 flex-1 truncate">
                {organization.name}
              </span>
              {organization.id === selectedOrganizationId && (
                <CheckIcon className="size-4 shrink-0" />
              )}
            </DropdownMenuItem>
          ))}
          {userType === "employer" && !pilotSelfServeLocked && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={openCreateModal}>
                <PlusIcon className="size-4" />
                Create organization
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {!pilotSelfServeLocked && (
        <CreateOrganizationModal
          isOpen={isCreateOpen}
          onOpenChange={handleCreateOpenChange}
        />
      )}
    </>
  );
}
